import React, { lazy, memo } from 'react';
interface PackVisualProps {
  title: string;
  themeColor: 'blue' | 'green' | 'pink' | 'purple' | 'teal' | 'navy';
  imageSrc?: string;
  priority?: boolean;
}
const themeMap = {
  blue: {
    main: 'bg-blue-600',
    dark: 'bg-blue-800',
    light: 'bg-blue-400',
    text: 'text-blue-100',
    border: 'border-blue-400'
  },
  green: {
    main: 'bg-emerald-700',
    dark: 'bg-emerald-900',
    light: 'bg-emerald-500',
    text: 'text-emerald-100',
    border: 'border-emerald-500'
  },
  pink: {
    main: 'bg-pink-500',
    dark: 'bg-pink-700',
    light: 'bg-pink-400',
    text: 'text-pink-100',
    border: 'border-pink-300'
  },
  purple: {
    main: 'bg-purple-600',
    dark: 'bg-purple-800',
    light: 'bg-purple-400',
    text: 'text-purple-100',
    border: 'border-purple-400'
  },
  teal: {
    main: 'bg-cyan-600',
    dark: 'bg-cyan-800',
    light: 'bg-cyan-400',
    text: 'text-cyan-100',
    border: 'border-cyan-400'
  },
  navy: {
    main: 'bg-slate-800',
    dark: 'bg-slate-950',
    light: 'bg-slate-500',
    text: 'text-slate-100',
    border: 'border-amber-400'
  }
} as const;
/**
 * Inject Cloudinary on-the-fly transforms:
 * f_auto (AVIF/WebP), q_auto (quality), dpr_auto (retina-aware), w_<size> (resize).
 */
function optimizeImageUrl(url: string, width: number): string {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  if (url.includes('/upload/f_auto')) return url;
  return url.replace('/upload/', `/upload/f_auto,q_auto,dpr_auto,w_${width}/`);
}
function PackVisualBase({
  title,
  themeColor,
  imageSrc,
  priority = false
}: PackVisualProps) {
  const colors = themeMap[themeColor];
  if (imageSrc) {
    const optimized = optimizeImageUrl(imageSrc, 500);
    const optimized2x = optimizeImageUrl(imageSrc, 800);
    return <div className="relative w-full h-64 lg:h-52 flex items-center justify-center mb-6 mt-2 overflow-hidden" data-id="element-12">
        <img src={optimized} srcSet={`${optimized} 1x, ${optimized2x} 2x`} alt={title} width={400} height={400} loading={priority ? 'eager' : 'lazy'} decoding={priority ? 'sync' : 'async'} fetchPriority={priority ? 'high' : 'low'} className="h-full w-full object-contain" data-id="element-13" />
      </div>;
  }
  return <div className="relative w-full h-48 lg:h-40 flex items-center justify-center mb-6 mt-2 overflow-hidden" data-id="element-14">
      <div className={`absolute w-20 h-28 lg:w-16 lg:h-24 ${colors.dark} rounded-md shadow-sm transform -rotate-[24deg] -translate-x-16 lg:-translate-x-12 translate-y-4 border border-white/10 opacity-80`} data-id="element-15" />
      <div className={`absolute w-20 h-28 lg:w-16 lg:h-24 ${colors.dark} rounded-md shadow-sm transform rotate-[24deg] translate-x-16 lg:translate-x-12 translate-y-4 border border-white/10 opacity-80`} data-id="element-16" />
      <div className={`absolute w-24 h-32 lg:w-20 lg:h-28 ${colors.main} rounded-md shadow-md transform -rotate-12 -translate-x-10 lg:-translate-x-8 translate-y-2 border border-white/20 flex flex-col items-center justify-between py-2`} data-id="element-17">
        <span className="text-[6px] font-bold text-white/70" data-id="element-18">TOMO 1</span>
        <div className={`w-14 h-14 lg:w-12 lg:h-12 ${colors.light} rounded-sm opacity-40`} data-id="element-19" />
      </div>
      <div className={`absolute w-24 h-32 lg:w-20 lg:h-28 ${colors.main} rounded-md shadow-md transform rotate-12 translate-x-10 lg:translate-x-8 translate-y-2 border border-white/20 flex flex-col items-center justify-between py-2`} data-id="element-20">
        <span className="text-[6px] font-bold text-white/70" data-id="element-21">TOMO 2</span>
        <div className={`w-14 h-14 lg:w-12 lg:h-12 ${colors.light} rounded-sm opacity-40`} data-id="element-22" />
      </div>
      <div className={`absolute w-28 h-36 lg:w-24 lg:h-32 ${colors.main} rounded-md shadow-lg transform -rotate-6 -translate-x-5 lg:-translate-x-4 translate-y-1 border border-white/30 flex flex-col items-center justify-between py-2 z-0`} data-id="element-23">
        <span className="text-[7px] font-bold text-white/80" data-id="element-24">TOMO 3</span>
        <div className={`w-16 h-16 lg:w-14 lg:h-14 ${colors.light} rounded-sm opacity-50`} data-id="element-25" />
      </div>
      <div className={`absolute w-28 h-36 lg:w-24 lg:h-32 ${colors.main} rounded-md shadow-lg transform rotate-6 translate-x-5 lg:translate-x-4 translate-y-1 border border-white/30 flex flex-col items-center justify-between py-2 z-0`} data-id="element-26">
        <span className="text-[7px] font-bold text-white/80" data-id="element-27">TOMO 4</span>
        <div className={`w-16 h-16 lg:w-14 lg:h-14 ${colors.light} rounded-sm opacity-50`} data-id="element-28" />
      </div>
      <div className={`absolute z-10 w-32 h-44 lg:w-28 lg:h-40 ${colors.main} rounded-lg shadow-2xl border-2 ${colors.border} flex flex-col items-center justify-start pt-4 overflow-hidden`} data-id="element-29">
        <div className="text-[8px] font-bold text-yellow-300 tracking-widest mb-1" data-id="element-30">
          ULTRAPACK
        </div>
        <div className="text-sm lg:text-[13px] font-extrabold text-white text-center leading-tight px-2 mb-2" data-id="element-31">
          {title}
        </div>
        <div className={`w-[85%] h-20 lg:h-16 ${colors.dark} rounded-md mt-auto mb-3 relative overflow-hidden flex items-center justify-center`} data-id="element-32">
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white to-transparent" data-id="element-33" />
          <div className="w-10 h-10 lg:w-8 lg:h-8 rounded-full bg-white/20 border border-white/30 flex items-center justify-center" data-id="element-34">
            <div className="w-5 h-5 lg:w-4 lg:h-4 rounded-full bg-white/50" data-id="element-35" />
          </div>
        </div>
        <div className="absolute bottom-2 right-2 w-8 h-8 lg:w-7 lg:h-7 bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-600 rounded-full border border-yellow-200 shadow-md flex items-center justify-center transform rotate-12" data-id="element-36">
          <span className="text-[5px] font-extrabold text-yellow-900 leading-none text-center" data-id="element-37">
            PRO
            <br data-id="element-38" />
            PACK
          </span>
        </div>
      </div>
    </div>;
}
export const PackVisual = memo(PackVisualBase);